import {
  CareerSiteBrandConfigurationV2Response,
  CareerSiteFiltersOption,
  CareerSiteOpeningV2Response,
} from 'api-clients/monolith';
import { ActiveFilters, AppContext, Segment } from 'contexts/app-context'; // V1
import { FeatureFlagContext } from 'contexts/feature-flag-context';
import {
  ActiveFiltersV2,
  AppContext as AppContextV2,
  FilterCounts,
  getDefaultActiveFilters,
  Segment as SegmentV2,
} from 'contexts/v2/app-context';
import { useLanguage } from 'i18n/LanguageProvider';
import queryString from 'query-string';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';

import { useWindowResize } from 'hooks/useWindowResize';

const defaultLocPosState = { id: null, value: null };

export const defaultFilterData: ActiveFilters = {
  compensationType: { key: 'any', label: 'Any' },
  sortBy: { key: 'distance', label: 'Distance' },
  radius: { key: 'any', label: 'Any' },
  category: { key: 'any', label: 'Any' },
  department: { key: 'all', label: 'All' },
  location: { key: 'current_location', label: 'Current Location' },
  position: { key: 'all', label: 'All' },
};

const defaultFilterDataV2: ActiveFiltersV2 = {
  category: [],
  city: [],
  country: [],
  jobHours: [],
  jobType: [],
  position: [],
  locationName: [],
  state: [],
};

interface AppContextProviderProps {
  children: ReactNode;
}

function V2Provider({
  children,
  brandConfig,
}: {
  children: ReactNode;
  brandConfig?: CareerSiteBrandConfigurationV2Response;
}) {
  const { currentLanguage } = useLanguage();
  const [accountSlug, setAccountSlug] = useState('');
  const [brandId, setBrandId] = useState<string | undefined>(undefined);
  const [brandName, setBrandName] = useState('');
  const [activeFiltersV2, setActiveFiltersV2] =
    useState<ActiveFiltersV2>(defaultFilterDataV2);
  const [location, setLocation] = useState<SegmentV2>(defaultLocPosState);
  const [displayLocation, setDisplayLocation] = useState<string | null>(null);
  const [position, setPosition] = useState<SegmentV2>(defaultLocPosState);
  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [experienceLevelName, setExperienceLevelName] = useState<string | null>(
    null,
  );
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [jobSearchQuery, setJobSearchQuery] = useState<string | null>(null);
  const [isFetchingJobs, setIsFetchingJobs] = useState(false);
  const [page, setPage] = useState(1);
  const [isJobsFromCurrentLocation, setIsJobsFromCurrentLocation] =
    useState(true);
  const [radius, setRadius] = useState<CareerSiteFiltersOption | null>(null);
  const [jobs, setJobs] = useState<CareerSiteOpeningV2Response['openings']>([]);
  const [paginationData, setPaginationData] = useState<
    CareerSiteOpeningV2Response['pagination'] | undefined
  >(undefined);
  const [currentCity, setCurrentCity] = useState<string | null>(null);
  const [currentState, setCurrentState] = useState<string | null>(null);
  const [currentCountry, setCurrentCountry] = useState<string | null>(null);
  const [filterCounts, setFilterCounts] = useState<FilterCounts | null>(null);
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [pendingFiltersV2, setPendingFiltersV2] = useState<ActiveFiltersV2>(
    getDefaultActiveFilters(),
  );

  useEffect(() => {
    if (brandConfig?.radius_options?.[0]) {
      const defaultRadius =
        brandConfig.radius_options.find(opt => opt.key.startsWith('50')) ??
        brandConfig.radius_options[0];
      setRadius(defaultRadius);
    }
  }, [brandConfig?.radius_options]);

  const [sortBy, setSortBy] = useState<CareerSiteFiltersOption>({
    key: 'date_posted',
    label: 'Date Posted',
  });
  const { isMobile } = useWindowResize();

  const history = useHistory();

  const setBrandDetails = useCallback(
    (accountSlug: string, brandId: string | undefined, brandName: string) => {
      setAccountSlug(accountSlug);
      setBrandId(brandId);
      setBrandName(brandName);
    },
    [],
  );

  const setDefaultFilterData = useCallback((data: ActiveFiltersV2) => {
    setActiveFiltersV2(data);
  }, []);

  const resetUrlParams = useCallback(
    (accountSlug: string, brandId: string | undefined) => {
      history.push({
        pathname: brandId ? `/${accountSlug}/${brandId}` : `/${accountSlug}`,
        search: '',
      });
    },
    [history],
  );

  const pendingUpdatesRef = React.useRef<Record<string, string | string[]>>({});
  const flushTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const flushUrlUpdates = useCallback(
    (accountSlug: string, brandId: string | undefined) => {
      if (Object.keys(pendingUpdatesRef.current).length === 0) return;

      const currentSearch = history.location.search;
      const currentParams = queryString.parse(currentSearch, {
        arrayFormat: 'bracket',
        parseNumbers: false,
        parseBooleans: false,
      });

      const updatedParams = { ...currentParams };

      Object.entries(pendingUpdatesRef.current).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length === 0) {
          delete updatedParams[key];
        } else if (value === '' || value === null) {
          delete updatedParams[key];
        } else {
          updatedParams[key] = value;
        }
      });

      pendingUpdatesRef.current = {};

      const nextSearch = queryString.stringify(updatedParams, {
        arrayFormat: 'bracket',
        skipNull: true,
        skipEmptyString: true,
        encode: true,
      });

      history.push({
        pathname: brandId ? `/${accountSlug}/${brandId}` : `/${accountSlug}`,
        search: nextSearch,
      });
    },
    [history],
  );

  const updateUrlWithParams = useCallback(
    (
      accountSlug: string,
      brandId: string | undefined,
      key: string,
      value: string | string[],
    ) => {
      pendingUpdatesRef.current[key] = value;

      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }

      flushTimeoutRef.current = setTimeout(() => {
        flushUrlUpdates(accountSlug, brandId);
        flushTimeoutRef.current = null;
      }, 0);
    },
    [flushUrlUpdates],
  );

  const replaceUrlWithParams = useCallback(
    (
      accountSlug: string,
      brandId: string | undefined,
      key: string,
      value: string | string[],
    ) => {
      const currentSearch = history.location.search;
      const currentParams = queryString.parse(currentSearch, {
        arrayFormat: 'bracket',
        parseNumbers: false,
        parseBooleans: false,
      });

      const updatedParams = { ...currentParams };

      if (Array.isArray(value) && value.length === 0) {
        delete updatedParams[key];
      } else if (value === '' || value === null) {
        delete updatedParams[key];
      } else {
        updatedParams[key] = value;
      }

      const nextSearch = queryString.stringify(updatedParams, {
        arrayFormat: 'bracket',
        skipNull: true,
        skipEmptyString: true,
        encode: true,
      });

      history.replace({
        pathname: brandId ? `/${accountSlug}/${brandId}` : `/${accountSlug}`,
        search: nextSearch,
      });
    },
    [history],
  );

  const resetFilters = useCallback(() => {
    setActiveFiltersV2(() => ({
      ...defaultFilterDataV2,
    }));
    setPage(1);
    if (accountSlug) {
      Object.keys(defaultFilterDataV2).forEach(key => {
        updateUrlWithParams(accountSlug, brandId, key, []);
      });
      updateUrlWithParams(accountSlug, brandId, 'page', '1');
    }
  }, [accountSlug, brandId, updateUrlWithParams]);

  const ctxSetActiveFilters = useCallback(
    (updater: React.SetStateAction<ActiveFiltersV2>) => {
      setActiveFiltersV2(updater);
    },
    [],
  );

  return (
    <AppContextV2.Provider
      value={{
        accountSlug,
        brandId,
        brandName,
        radius,
        setRadius,
        sortBy,
        setSortBy,
        activeFiltersV2,
        setActiveFiltersV2: ctxSetActiveFilters,
        location,
        setLocation,
        displayLocation,
        setDisplayLocation,
        position,
        setPosition,
        categoryName,
        setCategoryName,
        experienceLevelName,
        setExperienceLevelName,
        setBrandDetails,
        placeId,
        setPlaceId,
        jobSearchQuery,
        setJobSearchQuery,
        isFetchingJobs,
        setIsFetchingJobs,
        resetFilters,
        page,
        setPage,
        isMobile,
        isJobsFromCurrentLocation,
        setIsJobsFromCurrentLocation,
        setDefaultFilterData,
        updateUrlWithParams,
        replaceUrlWithParams,
        resetUrlParams,
        currentLanguage,
        setCurrentLanguage: () => undefined,
        jobs,
        setJobs,
        paginationData,
        setPaginationData,
        currentCity,
        setCurrentCity,
        currentState,
        setCurrentState,
        currentCountry,
        setCurrentCountry,
        filterCounts,
        setFilterCounts,
        isCurrentLocation: isJobsFromCurrentLocation,
        isFiltersDrawerOpen,
        setIsFiltersDrawerOpen,
        pendingFiltersV2,
        setPendingFiltersV2,
      }}
    >
      {children}
    </AppContextV2.Provider>
  );
}

function V1Provider({ children }: { children: ReactNode }) {
  const { currentLanguage } = useLanguage();
  const [accountSlug, setAccountSlug] = useState('');
  const [brandId, setBrandId] = useState<string | undefined>(undefined);
  const [brandName, setBrandName] = useState('');
  const [activeFilters, setActiveFilters] =
    useState<ActiveFilters>(defaultFilterData);
  const [location, setLocation] = useState<Segment>(defaultLocPosState);
  const [position, setPosition] = useState<Segment>(defaultLocPosState);
  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [experienceLevelName, setExperienceLevelName] = useState<string | null>(
    null,
  );
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [jobSearchQuery, setJobSearchQuery] = useState<string | null>(null);
  const [isFetchingJobs, setIsFetchingJobs] = useState(false);
  const [page, setPage] = useState(1);
  const [isJobsFromCurrentLocation, setIsJobsFromCurrentLocation] =
    useState(true);
  const { isMobile } = useWindowResize();

  const history = useHistory();

  const setBrandDetails = useCallback(
    (accountSlug: string, brandId: string | undefined, brandName: string) => {
      setAccountSlug(accountSlug);
      setBrandId(brandId);
      setBrandName(brandName);
    },
    [],
  );

  const setDefaultFilterData = useCallback((data: ActiveFilters) => {
    setActiveFilters(data);
  }, []);

  const resetUrlParams = useCallback(
    (accountSlug: string, brandId: string | undefined) => {
      const updatedParams = {};
      history.push({
        pathname: brandId ? `/${accountSlug}/${brandId}` : `/${accountSlug}`,
        search: queryString.stringify(updatedParams),
      });
    },
    [history],
  );

  const resetFilters = useCallback(() => {
    setActiveFilters({
      ...defaultFilterData,
      location: {
        key: 'current_location',
        label: 'Current Location',
      },
    });
    setPosition(defaultLocPosState);
    setCategoryName(null);
    setExperienceLevelName(null);
    setPlaceId(null);
    setJobSearchQuery(null);
    setPage(1);
    setIsJobsFromCurrentLocation(false);

    // Clear URL parameters when resetting filters
    resetUrlParams(accountSlug, brandId);
  }, [accountSlug, brandId, resetUrlParams]);

  const updateUrlWithParams = (
    accountSlug: string,
    brandId: string | undefined,
    key: string,
    value: string | number,
  ) => {
    const currentSearch = history.location.search;
    const currentParams = queryString.parse(currentSearch);
    const updatedParams = { ...currentParams, [key]: value };

    if (key === 'location') {
      delete updatedParams.place_id;
    }

    if (key === 'place_id') {
      delete updatedParams.location;
    }

    const nextPathname = brandId
      ? `/${accountSlug}/${brandId}`
      : `/${accountSlug}`;
    const nextSearch = queryString.stringify(updatedParams);

    history.push({
      pathname: nextPathname,
      search: nextSearch,
    });
  };

  return (
    <AppContext.Provider
      value={{
        accountSlug,
        brandId,
        brandName,
        activeFilters,
        setActiveFilters,
        location,
        setLocation,
        position,
        setPosition,
        categoryName,
        setCategoryName,
        experienceLevelName,
        setExperienceLevelName,
        setBrandDetails,
        placeId,
        setPlaceId,
        jobSearchQuery,
        setJobSearchQuery,
        isFetchingJobs,
        setIsFetchingJobs,
        resetFilters,
        page,
        setPage,
        isMobile,
        isJobsFromCurrentLocation,
        setIsJobsFromCurrentLocation,
        setDefaultFilterData,
        updateUrlWithParams,
        resetUrlParams,
        currentLanguage,
        setCurrentLanguage: () => undefined,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function AppContextProvider({
  children,
  brandConfig,
}: AppContextProviderProps & {
  brandConfig?: CareerSiteBrandConfigurationV2Response;
}) {
  const { featureFlags } = useContext(FeatureFlagContext);

  return featureFlags.careerSiteInterdependentFilters ? (
    <V2Provider brandConfig={brandConfig}>{children}</V2Provider>
  ) : (
    <V1Provider>{children}</V1Provider>
  );
}
